import { useState } from "react";
import { Phone, Mail, MapPin, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import QuoteDialog from "@/components/QuoteDialog";

const initial = { name: "", email: "", phone: "", company: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);

  const handle = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Mensaje enviado", { description: "Te responderemos a la brevedad." });
      setForm(initial);
    } catch (err) {
      toast.error("Error al enviar", { description: err?.response?.data?.detail || "Intenta nuevamente." });
    } finally {
      setSubmitting(false);
    }
  };

  const labelCls = "block text-xs font-display font-bold text-slate-700 mb-1.5 uppercase tracking-wider";
  const inputCls = "w-full border border-slate-300 rounded-none px-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white text-slate-900 transition-shadow";

  return (
    <div className="bg-slate-50 min-h-screen">
      <section className="bg-slate-950 text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24">
          <div className="font-mono text-xs tracking-[0.3em] uppercase text-amber-500 mb-3">Contacto</div>
          <h1 className="font-display text-4xl md:text-6xl font-black uppercase tracking-tighter">
            Conversemos sobre<br /><span className="text-amber-500">tu próximo proyecto.</span>
          </h1>
          <p className="mt-6 text-slate-400 max-w-2xl text-lg">
            ¿Listo para cotizar maquinaria? Completa el formulario o escríbenos directamente.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
        {/* Form */}
        <div className="lg:col-span-7 bg-white border-2 border-slate-950 p-6 md:p-10">
          <h2 className="font-display text-2xl md:text-3xl font-bold uppercase tracking-tight mb-1">Envíanos un mensaje</h2>
          <p className="text-sm text-slate-500 mb-6">Si necesitas cotización formal con datos de empresa, usa el formulario de cotización.</p>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="contact-form">
            <div>
              <label className={labelCls}>Nombre *</label>
              <input required name="name" value={form.name} onChange={handle} className={inputCls} data-testid="contact-input-name" />
            </div>
            <div>
              <label className={labelCls}>Email *</label>
              <input required type="email" name="email" value={form.email} onChange={handle} className={inputCls} data-testid="contact-input-email" />
            </div>
            <div>
              <label className={labelCls}>Teléfono</label>
              <input name="phone" value={form.phone} onChange={handle} className={inputCls} data-testid="contact-input-phone" />
            </div>
            <div>
              <label className={labelCls}>Empresa</label>
              <input name="company" value={form.company} onChange={handle} className={inputCls} data-testid="contact-input-company" />
            </div>
            <div className="md:col-span-2">
              <label className={labelCls}>Mensaje *</label>
              <textarea required rows={5} name="message" value={form.message} onChange={handle} className={inputCls} data-testid="contact-input-message" />
            </div>
            <div className="md:col-span-2 flex flex-wrap items-center gap-3 pt-2">
              <button type="submit" disabled={submitting} className="bg-slate-950 text-white font-display font-black uppercase tracking-wider px-6 py-4 text-sm hover:bg-slate-800 transition-colors disabled:opacity-60 flex items-center gap-2" data-testid="contact-submit-btn">
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                Enviar mensaje
              </button>
              <button type="button" onClick={() => setQuoteOpen(true)} data-testid="contact-cotizar-btn" className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-display font-black uppercase tracking-wider px-6 py-4 text-sm btn-brutal">
                Cotizar maquinaria
              </button>
            </div>
          </form>
        </div>

        {/* Info */}
        <div className="lg:col-span-5 space-y-5">
          <div className="bg-amber-500 text-slate-950 p-6 border-2 border-slate-950">
            <div className="font-mono text-xs uppercase tracking-[0.2em] opacity-70">Atención directa</div>
            <div className="font-display font-black text-3xl uppercase tracking-tight mt-1">Habla con un asesor</div>
          </div>
          <div className="bg-white border border-slate-200 p-5 flex items-start gap-4">
            <Phone className="w-6 h-6 text-amber-500 mt-1 shrink-0" />
            <div>
              <div className="text-xs font-display font-bold uppercase tracking-wider text-slate-500">Teléfono</div>
              <a href="tel:+56961018000" className="font-mono text-lg text-slate-950 hover:text-amber-600 transition-colors">+569 6101 8000</a>
            </div>
          </div>
          <div className="bg-white border border-slate-200 p-5 flex items-start gap-4">
            <Mail className="w-6 h-6 text-amber-500 mt-1 shrink-0" />
            <div>
              <div className="text-xs font-display font-bold uppercase tracking-wider text-slate-500">Email comercial</div>
              <a href="mailto:contacto@gwmlatam.com" className="font-mono text-sm text-slate-950 hover:text-amber-600 transition-colors break-all">
                contacto@gwmlatam.com
              </a>
            </div>
          </div>
          <div className="bg-white border border-slate-200 p-5 flex items-start gap-4">
            <MapPin className="w-6 h-6 text-amber-500 mt-1 shrink-0" />
            <div>
              <div className="text-xs font-display font-bold uppercase tracking-wider text-slate-500">Cobertura</div>
              <div className="text-slate-950">Latinoamérica · España · Portugal</div>
            </div>
          </div>
        </div>
      </div>

      <QuoteDialog open={quoteOpen} onOpenChange={setQuoteOpen} product={null} />
    </div>
  );
}
